import { css } from '@emotion/css';

import { cn } from '@lyearn/style';

import {
  BoxRadioButtonStyles,
  RadioButtonAppearance,
  RadioButtonSizes,
  TextRadioButtonStyles,
} from './types';

const disabledRadioStyles = css`
  .outer-circle {
    border-color: var(--border-dark);
  }
`;

const defaultSizeBoxDoubleStyles: BoxRadioButtonStyles = {
  main: {
    className: 'p-20 rounded-16',
  },
  icon: {
    className: 'w-40 h-40 mr-16',
  },
  label: {
    variant: 'body-short-01',
  },
  description: {
    variant: 'body-short-02',
  },
  radio: {
    size: 'large',
  },
};

const smallSizeBoxDoubleStyles: BoxRadioButtonStyles = {
  main: {
    className: 'p-16 rounded-12',
  },
  label: {
    variant: 'body-short-02',
  },
  description: {
    variant: 'body-short-03',
  },
  icon: {
    className: 'w-36 h-36 mr-12',
  },
  radio: {
    size: 'medium',
  },
};

const defaultSizeBoxSingleStyles: BoxRadioButtonStyles = {
  main: {
    className: 'px-16 py-12 rounded-12',
  },
  icon: {
    className: 'w-20 h-20 mr-12',
  },
  label: {
    variant: 'body-short-01',
  },
  radio: {
    size: 'medium',
  },
};

const smallSizeBoxSingleStyles: BoxRadioButtonStyles = {
  main: {
    className: 'px-12 py-10 rounded-12',
  },
  icon: {
    className: 'w-16 h-16 mr-8',
  },
  label: {
    variant: 'body-short-02',
  },
  radio: {
    size: 'x-small',
  },
};

const defaultSizeTextDoubleStyles: TextRadioButtonStyles = {
  gapY: 4,
  label: {
    variant: 'body-short-01',
  },
  description: {
    variant: 'body-short-02',
  },
  radio: {
    size: 'small',
    className: 'mr-12 mt-[0.3rem]',
  },
};

const smallSizeTextDoubleStyles: TextRadioButtonStyles = {
  gapY: 2,
  label: {
    variant: 'body-short-02',
  },
  description: {
    variant: 'body-short-02',
  },
  radio: {
    size: 'small',
    className: 'mr-12 mt-[0.3rem]',
  },
};

const defaultSizeTextSingleStyles: TextRadioButtonStyles = {
  label: {
    variant: 'body-short-01',
  },
  radio: {
    size: 'medium',
    className: 'mr-12',
  },
};

const smallSizeTextSingleStyles: TextRadioButtonStyles = {
  label: {
    variant: 'body-short-02',
  },
  radio: {
    size: 'x-small',
    className: 'mr-8',
  },
};

export const boxRadioButtonStyles: {
  [key in RadioButtonAppearance]: {
    [key in RadioButtonSizes]: BoxRadioButtonStyles;
  };
} = {
  double: {
    default: defaultSizeBoxDoubleStyles,
    small: smallSizeBoxDoubleStyles,
  },
  single: {
    default: defaultSizeBoxSingleStyles,
    small: smallSizeBoxSingleStyles,
  },
};

export const textRadioButtonStyles: {
  [key in RadioButtonAppearance]: {
    [key in RadioButtonSizes]: TextRadioButtonStyles;
  };
} = {
  double: {
    default: defaultSizeTextDoubleStyles,
    small: smallSizeTextDoubleStyles,
  },
  single: {
    default: defaultSizeTextSingleStyles,
    small: smallSizeTextSingleStyles,
  },
};

export const getBoxRadioButtonStyles = ({
  appearance,
  isDisabled,
  isSelected,
  size,
  className,
}: {
  appearance: RadioButtonAppearance;
  size: RadioButtonSizes;
  isSelected: boolean;
  isDisabled?: boolean;
  className?: string;
}) => {
  const baseStyles = boxRadioButtonStyles[appearance][size];

  return {
    ...baseStyles,
    main: {
      className: cn(
        'group border bg-bg-primary cursor-pointer',
        baseStyles.main.className,
        {
          'border-border hover:border-border-medium active:border-border-dark':
            !isDisabled && !isSelected,
          'opacity-50 pointer-events-none': isDisabled,
          'border-focus-primary': isSelected,
          'border-border-dark': isDisabled && !isSelected,
        },
        className,
      ),
    },
    icon: {
      className: cn(
        'flex-shrink-0',
        baseStyles.icon.className,
        isSelected ? 'text-icon-active' : 'text-icon-primary',
      ),
    },
    labelWrapper: appearance === 'double' ? 'mr-32' : 'mr-12',
    radio: {
      ...baseStyles.radio,
      className: cn('ml-auto', {
        [disabledRadioStyles]: isDisabled && !isSelected,
      }),
    },
  };
};
